import { Controller } from '@hotwired/stimulus'

const SEEN_KEY = 'portal-tour-seen'

const COPY = {
  welcome: {
    title: 'Welcome to your coaching portal',
    body: 'A short look at where things live. You can open this again from Tutorial in the sidebar.'
  },
  journey: {
    assignment: {
      title: 'Your coaching journey',
      body: 'This is where your coaching journey lives. '
        + 'You will be taken to the latest step in your journey when you click here.'
    },
    dropin: {
      title: 'Your coaching journey',
      body: 'This is where your coaching journey lives. Your drop-in sessions sit in this sidebar.'
    },
    empty: {
      title: 'Your coaching journey',
      body: 'This is where your coaching journey will live once it is set up.'
    }
  },
  profile: {
    title: 'Your profile',
    body: 'Your profile details live here. Edit your details any time.'
  },
  documents: {
    title: 'Documents',
    body: 'Upload documents here to share them with your coach as part of your development journey.'
  },
  companion: {
    title: 'Learning companion',
    body: 'Your AI learning companion is here whenever you want a hand.'
  }
}

function buildTourSteps() {
  const steps = [{ copy: COPY.welcome }]
  const journey = document.querySelector('[data-portal-tour="journey"]')
  if (journey) {
    const kind = journey.dataset.portalTourKind
    steps.push({ target: journey, copy: COPY.journey[kind] || COPY.journey.empty })
  }
  ;['profile', 'documents', 'companion'].forEach((key) => {
    const target = document.querySelector(`[data-portal-tour="${key}"]`)
    if (target) steps.push({ target, copy: COPY[key] })
  })
  return steps
}

function ensureNavOpen() {
  const nav = document.getElementById('portal-nav')
  const menu = document.querySelector('.portal-menu')
  const desktop = window.matchMedia('(min-width: 900px)').matches
  if (!nav || desktop || nav.classList.contains('is-open')) return false
  nav.classList.add('is-open')
  if (menu) menu.setAttribute('aria-expanded', 'true')
  return true
}

function tourSeen() {
  try {
    return localStorage.getItem(SEEN_KEY) === 'true'
  } catch (_error) {
    return false
  }
}

function rememberTour() {
  try {
    localStorage.setItem(SEEN_KEY, 'true')
  } catch (_error) {
    // A blocked store just means the tour can appear again next visit.
  }
}

export default class extends Controller {
  static values = { auto: Boolean }

  connect() {
    this.onKeydown = this.onKeydown.bind(this)
    this.reposition = this.reposition.bind(this)
    this.maybeOpen = this.maybeOpen.bind(this)
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.maybeOpen, { once: true })
    } else {
      this.maybeOpen()
    }
  }

  disconnect() {
    document.removeEventListener('DOMContentLoaded', this.maybeOpen)
    this.teardown()
  }

  maybeOpen() {
    if (!this.autoValue || tourSeen() || document.querySelector('.modal-background')) return
    this.open()
  }

  open() {
    if (this.root) this.teardown()
    this.steps = buildTourSteps()
    this.index = 0
    this.returnFocus = document.activeElement
    this.openedNav = ensureNavOpen()
    this.mount()
    this.show()
  }

  next() {
    if (this.index >= this.steps.length - 1) {
      this.finish()
      return
    }
    this.index += 1
    this.show()
  }

  finish() {
    rememberTour()
    this.teardown()
  }

  mount() {
    const root = document.createElement('div')
    root.className = 'portal-tour'
    root.innerHTML = `
      <div class="portal-tour-catch"></div>
      <div class="portal-tour-ring" hidden></div>
      <div class="portal-tour-card" role="dialog" aria-modal="true" aria-labelledby="portal-tour-title">
        <p class="portal-tour-kicker" id="portal-tour-step"></p>
        <h2 id="portal-tour-title" tabindex="-1"></h2>
        <p class="portal-tour-body"></p>
        <div class="portal-tour-actions">
          <button type="button" class="portal-tour-skip">Skip</button>
          <button type="button" class="portal-tour-next">Next</button>
        </div>
      </div>
    `
    this.root = root
    this.ring = root.querySelector('.portal-tour-ring')
    this.card = root.querySelector('.portal-tour-card')
    this.kicker = root.querySelector('.portal-tour-kicker')
    this.title = root.querySelector('#portal-tour-title')
    this.body = root.querySelector('.portal-tour-body')
    this.nextButton = root.querySelector('.portal-tour-next')
    root.querySelector('.portal-tour-skip').addEventListener('click', () => this.finish())
    this.nextButton.addEventListener('click', () => this.next())
    document.body.appendChild(root)
    document.addEventListener('keydown', this.onKeydown)
    window.addEventListener('resize', this.reposition)
    window.addEventListener('scroll', this.reposition, true)
  }

  show() {
    const step = this.steps[this.index]
    const last = this.index === this.steps.length - 1
    this.root.classList.toggle('is-welcome', !step.target)
    this.kicker.textContent = `Step ${this.index + 1} of ${this.steps.length}`
    this.title.textContent = step.copy.title
    this.body.textContent = step.copy.body
    this.nextButton.textContent = last ? 'Done' : 'Next'
    if (step.target) step.target.scrollIntoView({ block: 'nearest' })
    this.place(step)
    this.title.focus()
  }

  place(step) {
    if (!this.root) return
    if (!step.target) {
      this.ring.hidden = true
      this.card.style.left = ''
      this.card.style.top = ''
      return
    }
    const rect = step.target.getBoundingClientRect()
    const pad = 6
    this.ring.hidden = false
    this.ring.style.top = `${rect.top - pad}px`
    this.ring.style.left = `${rect.left - pad}px`
    this.ring.style.width = `${rect.width + pad * 2}px`
    this.ring.style.height = `${rect.height + pad * 2}px`
    this.placeCard(rect)
  }

  placeCard(rect) {
    const margin = 16
    const width = this.card.offsetWidth
    const height = this.card.offsetHeight
    let left = rect.right + margin
    let {top} = rect
    if (left + width > window.innerWidth - margin) {
      left = Math.min(rect.left, window.innerWidth - width - margin)
      top = rect.bottom + margin
    }
    if (top + height > window.innerHeight - margin) {
      top = rect.top - height - margin
    }
    this.card.style.left = `${Math.max(margin, left)}px`
    this.card.style.top = `${Math.max(margin, Math.min(top, window.innerHeight - height - margin))}px`
  }

  reposition() {
    if (!this.root || !this.steps) return
    this.place(this.steps[this.index])
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.finish()
      return
    }
    if (event.key !== 'Tab' || !this.card) return
    const items = [...this.card.querySelectorAll('button, h2')]
    const first = items[0]
    const last = items[items.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  teardown() {
    if (this.openedNav) {
      const nav = document.getElementById('portal-nav')
      const menu = document.querySelector('.portal-menu')
      if (nav) nav.classList.remove('is-open')
      if (menu) menu.setAttribute('aria-expanded', 'false')
      this.openedNav = false
    }
    document.removeEventListener('keydown', this.onKeydown)
    window.removeEventListener('resize', this.reposition)
    window.removeEventListener('scroll', this.reposition, true)
    if (this.root) this.root.remove()
    this.root = null
    const replay = document.querySelector('.portal-tour-open')
    const back = this.returnFocus && this.returnFocus.isConnected ? this.returnFocus : replay
    if (back) back.focus()
  }
}
